/**
 * "Iniciar conversa no WhatsApp" de dentro do ticket (spec 057): a aba "Conversas"
 * da visão completa e o compositor do atendimento usam o mesmo.
 *
 * - O número sugerido é o do disparo (resolveDispatchPhone): a ficha que uma pessoa
 *   editou, depois a da descrição, e o solicitante só do ticket aberto na web;
 *   senão, o primeiro contato do cadastro do cliente. Antes vinha o solicitante
 *   primeiro, e o do ticket importado do TiFlux é o número de um terceiro (F-79).
 * - A conversa nasce vinculada ao ticket e ao cliente dele (POST /chats/whatsapp
 *   com ticketNumber), na mesma transação. Antes era um segundo pedido, e a falha
 *   dele não aparecia.
 * - Número que já tem conversa aberta (409): ela pode ser de outro ticket, e
 *   vinculá-la a este é decisão de quem atende, pelo botão do aviso.
 *
 * Sem número sugerido o fluxo segue aberto, com o campo vazio e a dica: quem
 * atende costuma ter o número. O único bloqueio é não haver canal conectado.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link2, MessageSquarePlus } from 'lucide-react';
import {
  DISPATCH_PHONE_SOURCE_LABELS,
  resolveDispatchPhone,
  sendablePhone,
  type TicketDetail,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { clientDetailOptions } from '@/features/clients/clients-queries';
import { whatsappChannelsOptions } from '@/features/whatsapp/whatsapp-queries';
import { useUpdateAnyChat } from '@/features/chats/chats-mutations';
import { errorMessage } from '@/features/chats/chats-format';
import { NewWhatsappChatDialog } from '@/features/chats/new-whatsapp-chat-dialog';

export function StartWhatsappAction({
  ticket,
  onOpened,
  onExisting,
  compact = false,
}: {
  ticket: TicketDetail;
  /** a conversa aberta (nova, ou a que já existia e foi vinculada aqui) */
  onOpened: (chatId: number) => void;
  /** 409: a conversa que o número já tinha (a aba a mostra ao lado do aviso) */
  onExisting?: (chatId: number) => void;
  /** no cabeçalho do compositor: botão pequeno; na aba: bloco de largura cheia */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // 409: a conversa aberta que o número já tem, à espera de "vincular a este ticket"
  const [existingId, setExistingId] = useState<number | null>(null);
  const link = useUpdateAnyChat();

  // Sem canal (ou sem permissão no endpoint) o botão fica desabilitado, não some.
  const channelsQ = useQuery({ ...whatsappChannelsOptions(), retry: false, staleTime: 5 * 60_000 });
  const channels = (channelsQ.data ?? []).filter((c) => c.active);

  const fromTicket = resolveDispatchPhone(ticket);
  // o cadastro do cliente só é lido quando o ticket não tem número que preste
  const clientQ = useQuery({ ...clientDetailOptions(ticket.client.id), enabled: fromTicket === null, retry: false });
  const fromClient =
    fromTicket === null
      ? (clientQ.data?.contacts ?? []).reduce<string | null>((found, c) => found ?? sendablePhone(c.telephone), null)
      : null;
  const phone = fromTicket?.phone ?? fromClient;

  const loading = channelsQ.isPending || (fromTicket === null && clientQ.isPending && !clientQ.isError);
  const blocked =
    channels.length === 0
      ? 'Nenhum canal do WhatsApp conectado — configure um canal em Configurações › Integrações.'
      : null;
  // de onde veio o número sugerido; sem número, a dica de que falta digitar
  const phoneSource =
    fromTicket !== null
      ? `Número sugerido: ${DISPATCH_PHONE_SOURCE_LABELS[fromTicket.source]} do ticket #${ticket.ticketNumber}.`
      : fromClient !== null
        ? `Número sugerido: o contato de ${ticket.client.name}.`
        : null;
  const phoneHint =
    blocked === null && phone === null
      ? `O ticket e o cadastro de ${ticket.client.name} não têm telefone — informe o número para iniciar.`
      : null;

  const linkHere = () => {
    if (existingId === null) return;
    link.mutate(
      { chatId: existingId, ticketNumber: ticket.ticketNumber, clientId: ticket.client.id },
      {
        onSuccess: () => {
          onOpened(existingId);
          setExistingId(null);
        },
      }
    );
  };

  return (
    <>
      <Button
        variant={compact ? 'ghost' : 'outline'}
        size={compact ? 'xs' : 'sm'}
        className={cn(!compact && 'w-full')}
        disabled={loading || blocked !== null}
        title={blocked ?? undefined}
        onClick={() => {
          setExistingId(null);
          link.reset();
          setOpen(true);
        }}
      >
        {loading ? <Spinner className="size-4" /> : <MessageSquarePlus />}
        {compact ? 'Nova conversa no WhatsApp' : 'Iniciar conversa no WhatsApp'}
      </Button>
      {!compact && !loading && blocked !== null ? (
        <p className="mt-1.5 text-[11px] text-muted-foreground">{blocked}</p>
      ) : null}

      {existingId !== null ? (
        <div className="basis-full space-y-1.5 rounded-md border bg-muted/40 px-3 py-2 text-xs" role="status">
          <p>Este número já tem uma conversa aberta. Ela pode ser de outro ticket.</p>
          <div className="flex flex-wrap gap-2">
            <Button size="xs" variant="outline" onClick={linkHere} disabled={link.isPending}>
              {link.isPending ? <Spinner className="size-3" /> : <Link2 className="size-3" />}
              Vincular a este ticket
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setExistingId(null)}>
              Agora não
            </Button>
          </div>
          {link.error ? (
            <p className="text-destructive" role="alert">
              {errorMessage(link.error)}
            </p>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <NewWhatsappChatDialog
          channels={channels}
          link={{ ticketNumber: ticket.ticketNumber, clientId: ticket.client.id }}
          {...(phone !== null ? { initialPhone: phone } : {})}
          {...(phoneHint !== null ? { phoneHint } : {})}
          {...(phoneSource !== null ? { phoneSource } : {})}
          onClose={() => setOpen(false)}
          onOpened={(chatId, existing) => {
            setOpen(false);
            if (existing) {
              setExistingId(chatId);
              onExisting?.(chatId);
              return;
            }
            onOpened(chatId);
          }}
        />
      ) : null}
    </>
  );
}
